import React from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { textarea } from '../../utils/designTokens';
import Card from '../ui/Card';

/**
 * AgentFormStep2 - System Prompt Editor
 */
const AgentFormStep2 = ({ formData, onChange, errors }) => {
  const examplePrompts = [
    {
      title: 'Research Assistant',
      prompt: 'You are a helpful research assistant specialized in gathering, analyzing, and summarizing information from various sources. Your goal is to provide accurate, well-researched answers with proper citations.',
    },
    {
      title: 'Code Reviewer',
      prompt: 'You are an expert code reviewer. Analyze code for best practices, potential bugs, security issues, and performance optimizations. Provide constructive feedback with specific examples.',
    },
    {
      title: 'Creative Writer',
      prompt: 'You are a creative writing assistant. Help users craft compelling stories, develop characters, and refine their prose. Offer suggestions while preserving the user\'s unique voice.',
    },
  ];

  const loadExample = (prompt) => {
    onChange({ target: { name: 'system_prompt', value: prompt } });
  };

  return (
    <div className="space-y-6">
      {/* System Prompt */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-900 dark:text-spooky-ghost-white" htmlFor="system_prompt">
          System Prompt *
        </label>
        <textarea
          id="system_prompt"
          name="system_prompt"
          value={formData.system_prompt}
          onChange={onChange}
          placeholder="You are a helpful assistant that..."
          className={textarea}
          rows={12}
        />
        <div className="flex justify-between mt-1">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Define the agent's behavior, personality, and guidelines
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {formData.system_prompt.length} characters
          </p>
        </div>
        {errors.system_prompt && (
          <p className="text-sm text-red-500 mt-1">{errors.system_prompt}</p>
        )}
      </div>

      {/* Tips Sidebar */}
      <Card padding="comfortable" className="bg-pink-50 dark:bg-pink-900/10 border-2 border-pink-200 dark:border-pink-800">
        <div className="flex items-start gap-3">
          <FaInfoCircle className="text-pink-500 text-xl mt-1 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-spooky-ghost-white mb-2">
              System Prompt Tips
            </h4>
            <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
              <li>• Start with a clear role: "You are a..."</li>
              <li>• Define the agent's expertise and limitations</li>
              <li>• Specify the tone and communication style</li>
              <li>• Include any rules or guidelines to follow</li>
              <li>• Mention how to handle edge cases or errors</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Example Templates */}
      <div>
        <h4 className="text-sm font-semibold mb-3 text-gray-900 dark:text-spooky-ghost-white">
          Example System Prompts
        </h4>
        <div className="space-y-3">
          {examplePrompts.map((example, index) => (
            <Card key={index} padding="default" className="cursor-pointer hover:border-pink-300 dark:hover:border-pink-700 border-2 border-transparent transition-colors">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h5 className="font-medium text-gray-900 dark:text-spooky-ghost-white mb-1">
                    {example.title}
                  </h5>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {example.prompt}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => loadExample(example.prompt)}
                  className="ml-4 px-3 py-1 text-sm bg-pink-500 text-white rounded hover:bg-pink-600 transition flex-shrink-0"
                >
                  Use
                </button>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Preview Section */}
      {formData.system_prompt && (
        <div>
          <h4 className="text-sm font-semibold mb-2 text-gray-900 dark:text-spooky-ghost-white">
            Preview
          </h4>
          <Card padding="comfortable" className="bg-gray-50 dark:bg-spooky-bg-light">
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {formData.system_prompt}
            </p>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AgentFormStep2;
